import { type ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { clearToken, getToken } from '../api';

const NAV: Array<{ label: string; to: string; group?: 'main' | 'ops' | 'audit' }> = [
  { label: 'Cluster', to: '/cluster', group: 'main' },
  { label: 'Tenants', to: '/tenants', group: 'main' },
  { label: 'Sync-Steuerung', to: '/operations/sync-control', group: 'ops' },
  { label: 'Sync-Logs', to: '/operations/sync-logs', group: 'ops' },
  { label: 'Sync-State', to: '/operations/sync-state', group: 'ops' },
  { label: 'Outbox', to: '/operations/outbox', group: 'ops' },
  { label: 'Webhooks', to: '/operations/webhooks', group: 'ops' },
  { label: 'External API', to: '/external-access', group: 'ops' },
  { label: 'Audit (global)', to: '/audit', group: 'audit' },
];

export function Layout({ children }: { children: ReactNode }) {
  const loc = useLocation();
  const isAuthed = !!getToken();
  const [now, setNow] = useState<string>(() => formatNow());

  // einmal pro Minute Uhr aktualisieren
  if (typeof window !== 'undefined') {
    setTimeout(() => setNow(formatNow()), 60_000);
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-slate-800 text-neutral-100 border-b border-slate-900 px-3 py-1.5 flex items-center gap-4 text-[12px]">
        <Link to="/" className="font-semibold tracking-wide uppercase">
          Leitstand
        </Link>
        <span className="text-neutral-400">|</span>
        <span className="text-neutral-300">ePerformance Plattform-Admin</span>
        <div className="ml-auto flex items-center gap-3 text-neutral-300">
          <span className="font-mono text-[11px]">{now}</span>
          <span className="text-neutral-500">|</span>
          <span className="border border-amber-500 text-amber-300 px-1.5 py-0 text-[11px] uppercase">
            super_admin
          </span>
          {isAuthed && (
            <button
              type="button"
              className="text-neutral-300 hover:text-white"
              onClick={() => {
                clearToken();
                window.location.assign('/login');
              }}
            >
              Logout
            </button>
          )}
        </div>
      </header>

      <nav className="bg-neutral-100 border-b border-neutral-300 px-3 py-1 flex items-center gap-1 text-[12px]">
        {NAV.map((n) => {
          const active =
            n.to === '/cluster'
              ? loc.pathname === '/' || loc.pathname.startsWith('/cluster')
              : loc.pathname.startsWith(n.to);
          return (
            <Link
              key={n.to}
              to={n.to}
              className={
                'px-2 py-1 border-r border-neutral-300 ' +
                (active
                  ? 'bg-white text-slate-900 font-semibold border-t-2 border-t-slate-700 -mt-px'
                  : 'text-neutral-700 hover:bg-neutral-200')
              }
            >
              {n.label}
            </Link>
          );
        })}
      </nav>

      <Breadcrumb />

      <main className="flex-1 p-3 max-w-[1400px] w-full mx-auto">{children}</main>

      <footer className="bg-neutral-100 border-t border-neutral-300 px-3 py-1 text-[11px] text-neutral-600 flex items-center gap-3">
        <span>ePerformance Leitstand v0.1.0</span>
        <span className="text-neutral-400">|</span>
        <span>STACKIT eu01-de</span>
        <span className="text-neutral-400">|</span>
        <span className="font-mono">{location.host}</span>
      </footer>
    </div>
  );
}

function Breadcrumb() {
  const loc = useLocation();
  const parts = loc.pathname.split('/').filter(Boolean);
  if (parts.length === 0) return null;
  return (
    <div className="px-3 py-1 bg-white border-b border-neutral-300 text-[11px] text-neutral-600 font-mono">
      <Link to="/" className="hover:underline">
        /
      </Link>
      {parts.map((p, i) => {
        const path = '/' + parts.slice(0, i + 1).join('/');
        return (
          <span key={path}>
            {i > 0 && ' / '}
            {i === 0 && ' '}
            <Link to={path} className="hover:underline">
              {p}
            </Link>
          </span>
        );
      })}
    </div>
  );
}

function formatNow(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
}
